import { useEffect, useState } from 'react';
import { useFormV2 } from '@abyss/web/hooks/useForm';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { PageScrollService } from '@src/api/common/PageScrollService';
import { AlertService } from '@src/api/common/AlertService';
import { RedirectService } from '@src/api/common/RedirectService';
import { ApiStatus } from '@src/constants/ApiStatus';
import { AlertVariantType } from '@src/constants/AlertVariantType';
import { TechnologyService } from '@src/api/web-services/features/admin/Technology';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { Breadcrumbs, Title } from './PageInfo';
import { stringify } from 'querystring';

const TechnologyDetailController = () => {
  type Data = {
    item: any;
    detailResponse: any;
    isEdit: boolean;
  };

  const [pageData, setPageData] = useState<Data>({
    item: {},
    detailResponse: null,
    isEdit: false,
  });
  const { UpdateBreadcrumb } = BreadcrumbService();
  const { UpdatePageTitle } = PageTitleService();
  const [fieldsDisabled, setFieldsDisabled] = useState<boolean>(false);
  const { getRouteParams } = useRouter();
  const params = getRouteParams('');
  const { ScrollToTop, pageTop } = PageScrollService();
  const { AlertModel, ShowAlert } = AlertService();
  const { Redirect, RedirectDuration } = RedirectService();

  const {
    GetTechnologyById,
    CreateOrUpdateTechnology,
    LogicDeleteTechnologyById,
  } = TechnologyService();

  const form = useFormV2({
    defaultValues: {
      id: 0,
      name: '',
      isActive: false,
    },
    values: pageData.item,
  });

  const DataSource = {
    GoBackToList: () => {
      Redirect(`/admin/technology`, RedirectDuration.SHORT);
    },
    GetDetail: async () => {
      const body = {
        id: Number(params?.id ?? 0),
      };
      if (body.id === 0) {
        setFieldsDisabled(false);
        return;
      }
      const apiData = await GetTechnologyById(body);
      await setPageData((pageData) => ({
        ...pageData,
        detailResponse: apiData,
      }));
      if (apiData.status === ApiStatus.Success && apiData.data?.result) {
        let data = apiData.data.result;
        data.itemId = params.parentId;
        setPageData((p) => ({
          ...p,
          item: data,
        }));
      } else if (apiData.data?.message) {
        if (!apiData.data?.result) {
          setFieldsDisabled(true);
        }
        form.reset();
        AlertModel.variant = AlertVariantType.ERROR;
        AlertModel.message = apiData.data?.message;
        AlertModel.visible = true;
        AlertModel.isPermanent = true;
        await ShowAlert(AlertModel);
      }
    },
    DeleteItem: async () => {
      if (params?.id !== null) {
        const body = {
          id: Number(params?.id ?? 0),
        };
        const apiData = await LogicDeleteTechnologyById(body);

        if (apiData.data?.message) {
          AlertModel.variant =
            apiData.status === ApiStatus.Success
              ? AlertVariantType.SUCCESS
              : AlertVariantType.ERROR;
          AlertModel.message = apiData.data?.message;
          AlertModel.visible = true;
          await ShowAlert(AlertModel);
          if (apiData?.status === ApiStatus.Success) {
            form.reset();
            DataSource.GoBackToList();
          }
        }
      }
    },
    PostDetail: async () => {
      const body = form.getValues();
      const technology = {
        id: body.id ?? 0,
        name: body.name ?? '',
        isActive: !!body.isActive,
      };
      const apiData = await CreateOrUpdateTechnology(technology);
      if (
        (apiData?.status === ApiStatus.Success ||
          apiData?.status === ApiStatus.Created) &&
        apiData?.data?.result
      ) {
        let data = apiData.data.result;
        await setPageData((p) => ({
          ...p,
          item: data,
        }));
        if (data.id > 0) {
          DataSource.GoBackToList();
        }
      }

      if (apiData.data?.message) {
        AlertModel.variant =
          apiData.status === ApiStatus.Success || ApiStatus.Created
            ? AlertVariantType.SUCCESS
            : AlertVariantType.ERROR;
        AlertModel.message = apiData.data?.message;
        AlertModel.visible = true;
        await ShowAlert(AlertModel);
      }
    },
  };

  const Action = {
    Init: async () => {
      await UpdateBreadcrumb(Breadcrumbs);
      await UpdatePageTitle(Title);
      await Action.Load();
    },
    Load: async () => {
      await DataSource.GetDetail();
    },
    OnDeleteConfirm: async () => {
      await DataSource.DeleteItem();
      await ScrollToTop();
    },
    Save: async () => {
      await DataSource.PostDetail();
      await ScrollToTop();
    },
  };

  useEffect(() => {
    if (params?.id !== null) {
      setPageData((p) => ({
        ...p,
        item: {
          id: params.id,
        },
        isEdit: Number(params.id) > 0 ? true : false,
      }));

      Action.Init();
    }
  }, []);

  return {
    form,
    Action,
    pageTop,
    params,
    pageData,
    fieldsDisabled,
  };
};

export { TechnologyDetailController };
